/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'

import { AvaInterviewStatus, FirstPageBehaviors } from 'src/api'
import * as interviewSelectors from 'src/interview/selectors'
import { Loader, NotFound } from 'src/components'
import useSupport from 'src/utils/useSupport'
import {
  avaGetInterviewAction,
  avaInterviewSelectors,
  avaSettingsSelectors,
  avaStepSelectors,
  clearAvaInterviewAction,
  clearAvaIntroStepAction,
  clearAvaOnboardingStepAction,
  clearAvaStepAction,
  updateAvaIntroStepAction,
  updateAvaStepAction,
} from '../redux'

import IntroContainer, { IntroStep } from './containers/IntroContainer'
import OnboardingContainer from './containers/OnboardingContainer'
import { AvaStep } from './models'
import ReportContainer from './containers/ReportContainer'
import LandingContainer from './containers/LandingContainer'
import InterviewContainer from './containers/InterviewContainer'

function AvaLayout() {
  const dispatch = useDispatch()

  const avaStep = useSelector(avaStepSelectors.data)
  const interview = useSelector(avaInterviewSelectors.data)
  const agency = useSelector(interviewSelectors.agency)
  const avaSettings = useSelector(avaSettingsSelectors.data)

  const { openSupport, hideLauncher } = useSupport(true)
  const params = new URLSearchParams(useLocation().search)
  const { interviewToken } = useParams<{ interviewToken?: string }>()

  const skiplanding = params.get('sl')
  const checkoutSuccess = params.get('checkout_success')

  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    ;(window as any).intercomSettings = { alignment: 'right' }
  }, [])

  useEffect(() => {
    if (avaStep !== AvaStep.Landing) hideLauncher()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avaStep])

  useEffect(() => {
    if (skiplanding) hideLauncher()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skiplanding])

  const showSupportRef = useRef(false)

  useEffect(() => {
    if (showSupportRef.current !== avaSettings.showSupport) {
      showSupportRef.current = avaSettings.showSupport
      openSupport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avaSettings.showSupport])

  useEffect(() => {
    if (interviewToken) {
      dispatch(avaGetInterviewAction({ interviewToken }))
      
      // If returning from checkout success, refresh interview data after a short delay
      // to ensure webhook has processed
      if (checkoutSuccess === 'true') {
        const refreshTimer = setTimeout(() => {
          dispatch(avaGetInterviewAction({ interviewToken }))
        }, 2000) // Wait 2 seconds for webhook to process
        
        return () => {
          clearTimeout(refreshTimer)
          dispatch(clearAvaOnboardingStepAction)
          dispatch(clearAvaStepAction)
          dispatch(clearAvaIntroStepAction)
          dispatch(clearAvaInterviewAction)
        }
      }
    }

    return () => {
      dispatch(clearAvaOnboardingStepAction)
      dispatch(clearAvaStepAction)
      dispatch(clearAvaIntroStepAction)
      dispatch(clearAvaInterviewAction)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewToken, checkoutSuccess])

  useEffect(() => {
    if (!loaded && agency?.id) {
      if (!interviewToken) {
        if (agency.direct_users_first_page === FirstPageBehaviors.OrderForm)
          dispatch(updateAvaStepAction(AvaStep.Onboarding))
        setLoaded(true)
      } else {
        // We have an interviewToken
        if (interview) {
          // Interview exists - check if it has order_number or if we should still proceed
          if (interview.order_number) {
            // Interview has order_number, determine step based on status
            switch (interview.status) {
              case AvaInterviewStatus.IN_INTERVIEW:
                // After checkout, if interview is ready, proceed directly to interview step
                dispatch(updateAvaStepAction(AvaStep.Intro))
                dispatch(updateAvaIntroStepAction(IntroStep.GettingQuestions))
                setLoaded(true)
                break
              case AvaInterviewStatus.COMPLETED:
                dispatch(updateAvaStepAction(AvaStep.Report))
                setLoaded(true)
                break
              default:
                // For invited or in_intro status with order_number
                // If returning from checkout, show subscription success message
                if (checkoutSuccess === 'true') {
                  dispatch(updateAvaStepAction(AvaStep.Intro))
                  dispatch(updateAvaIntroStepAction(IntroStep.Subscription))
                } else {
                  // For invited or in_intro status, show onboarding or intro based on agency settings
                  if (agency.invited_users_first_page === FirstPageBehaviors.OrderForm)
                    dispatch(updateAvaStepAction(AvaStep.Onboarding))
                  else
                    dispatch(updateAvaStepAction(AvaStep.Intro))
                }
                setLoaded(true)
                break
            }
          } else {
            // Interview exists but no order_number yet
            // If returning from checkout success, wait a bit for webhook to process, then show subscription step
            if (checkoutSuccess === 'true') {
              // Don't set loaded yet - wait for interview refresh
              dispatch(updateAvaStepAction(AvaStep.Intro))
              dispatch(updateAvaIntroStepAction(IntroStep.Subscription))
              // Set loaded after a delay to allow interview to refresh
              setTimeout(() => setLoaded(true), 1000)
            } else {
              // Show intro step for cases where interview is still being created
              dispatch(updateAvaStepAction(AvaStep.Intro))
              setLoaded(true)
            }
          }
        } else {
          // Interview hasn't loaded yet, but we have a token
          // If returning from checkout success, show subscription step
          // Otherwise show intro step as default - the interview will load and update the step accordingly
          if (checkoutSuccess === 'true') {
            dispatch(updateAvaStepAction(AvaStep.Intro))
            dispatch(updateAvaIntroStepAction(IntroStep.Subscription))
          } else {
            dispatch(updateAvaStepAction(AvaStep.Intro))
          }
          setLoaded(true)
        }
      }
    } else if (loaded && interview?.order_number) {
      // If we're already loaded but interview data has loaded, check status and update step accordingly
      if (checkoutSuccess === 'true' && interview?.status === AvaInterviewStatus.IN_INTERVIEW) {
        // If returning from checkout and interview is ready, proceed directly to interview step
        dispatch(updateAvaStepAction(AvaStep.Intro))
        dispatch(updateAvaIntroStepAction(IntroStep.GettingQuestions))
      } else if (interview?.status === AvaInterviewStatus.COMPLETED) {
        // If interview is completed, show report
        dispatch(updateAvaStepAction(AvaStep.Report))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency, interview, interviewToken, loaded, checkoutSuccess])

  const renderStep = (currentStep: AvaStep) => {
    if (!agency.id && currentStep !== AvaStep.Report) return null

    if (!interviewToken && !agency.allow_direct_registration) {
      return <NotFound />
    }

    switch (currentStep) {
      case AvaStep.Landing:
        if (skiplanding) {
          return <OnboardingContainer />
        }
        return <LandingContainer />
      case AvaStep.Onboarding:
        return <OnboardingContainer />
      case AvaStep.Intro:
        return <IntroContainer />
      case AvaStep.Interview:
        return <InterviewContainer />
      default:
        return <ReportContainer />
    }
  }

  return !loaded ? <Loader /> : renderStep(avaStep)
}

export default AvaLayout
